import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { Alert, Badge, Button as AntButton, Card, List, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { CheckOutlined, CloseOutlined, StopOutlined, SyncOutlined } from "@ant-design/icons";
import { GetUsers, SetUsers, GetAccessRequests, ApproveAccessRequest, RejectAccessRequest, RevokeUser } from "../services/ApiService";

const { Text, Title } = Typography;

function UsersInput({ value, onChange }) {
  return (
    <Form.Control as="textarea" name='Users' rows={10}
      onChange={onChange} value={value.join("\r\n")} placeholder="Input user emails" />
  )
}

function SubmitButton({ value, onClick }) {
  let text = value.is_processing ? " Processing..." : "Submit"
  let disabled = value.is_processing
  return (
    <Button variant="primary" disabled={disabled} onClick={onClick}>
      <Spinner as="span" animation="border" size="sm"
        role="status" aria-hidden="true" hidden={!value.is_processing} />
      {text}
    </Button>
  )
}

function ManageUsers({ is_admin }) {
  const auth = useAuth();
  const [hasPermission, setHasPermission] = useState(false)
  const [userlist, setUserlist] = useState([])
  const [resultMessage, setResultMessage] = useState("")
  const [submitState, setSubmitState] = useState({ is_processing: false });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let users = await GetUsers(auth.user.id_token, is_admin)
        setHasPermission(true)
        setUserlist(users)
      } catch (err) {
        setResultMessage(err.message)
      }
    };
    fetchUsers();
  }, [auth.user.id_token, is_admin]);

  function handleUsersChange(event) {
    let users = event.target.value.split(/[\s,;]+/)
    setUserlist(users)
  }

  async function handleSubmit(event) {
    try {
      setSubmitState({ is_processing: true })
      let users = await SetUsers(auth.user.id_token, userlist, is_admin)
      setUserlist(users)
      setResultMessage("Users are updated successfully.")
    } catch (err) {
      setResultMessage(err.message)
    } finally {
      setSubmitState({ is_processing: false })
    }
  }

  const style = { color: 'red' }
  return hasPermission ? (
    <div>
      <h1>{is_admin ? "Manage Admin Users" : "Manage Users"}</h1>
      <p><UsersInput value={userlist} onChange={handleUsersChange} /></p>
      <p><SubmitButton value={submitState} onClick={handleSubmit} /></p>
      <p style={style}> {resultMessage}</p>
    </div>
  ) : (
    is_admin ? <p></p> : <p style={style}> {resultMessage}</p>
  )
}

function UsersTable() {
  const auth = useAuth();
  const token = auth.user.id_token;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [revoking, setRevoking] = useState({});

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await GetUsers(token, false);
      setUsers(result);
      setFetchError(null);
    } catch (e) {
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRevoke = async (email) => {
    setRevoking((r) => ({ ...r, [email]: true }));
    setActionError(null);
    try {
      await RevokeUser(token, email);
      await fetchUsers();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setRevoking((r) => ({ ...r, [email]: false }));
    }
  };

  // Hide silently if not admin
  if (!loading && fetchError && fetchError.toLowerCase().includes("not admin")) return null;
  if (!loading && fetchError) return null;

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <Text>{email}</Text>,
    },
    {
      title: "",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title={`Revoke access for ${record.email}?`}
          onConfirm={() => handleRevoke(record.email)}
          okText="Revoke"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <AntButton
            danger
            icon={<StopOutlined />}
            size="small"
            loading={revoking[record.email]}
          >
            Revoke
          </AntButton>
        </Popconfirm>
      ),
    },
  ];

  const dataSource = users.map((email) => ({ key: email, email }));

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Users with Access</Title>
          {!loading && <Badge count={users.length} showZero color="blue" />}
        </Space>
      }
      extra={
        <AntButton icon={<SyncOutlined />} size="small" onClick={fetchUsers} loading={loading}>
          Refresh
        </AntButton>
      }
    >
      {actionError && <Alert type="error" message={actionError} showIcon style={{ marginBottom: 12 }} />}
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        size="small"
      />
    </Card>
  );
}

function AccessRequests() {
  const auth = useAuth();
  const token = auth.user.id_token;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const result = await GetAccessRequests(token);
      setRequests(result);
      setError(null);
    } catch (e) {
      // Not an admin — silently hide this panel
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (email) => {
    setProcessing((p) => ({ ...p, [email]: "approving" }));
    try {
      await ApproveAccessRequest(token, email);
      setRequests((r) => r.filter((e) => e !== email));
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing((p) => ({ ...p, [email]: null }));
    }
  };

  const handleReject = async (email) => {
    setProcessing((p) => ({ ...p, [email]: "rejecting" }));
    try {
      await RejectAccessRequest(token, email);
      setRequests((r) => r.filter((e) => e !== email));
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing((p) => ({ ...p, [email]: null }));
    }
  };

  // Hide panel silently if user is not admin
  if (!loading && error && error.toLowerCase().includes("not admin")) return null;
  if (!loading && error) return null;

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Pending Access Requests</Title>
          {requests.length > 0 && <Badge count={requests.length} />}
        </Space>
      }
      extra={
        <AntButton icon={<SyncOutlined />} size="small" onClick={fetchRequests} loading={loading}>
          Refresh
        </AntButton>
      }
    >
      {loading ? (
        <Text type="secondary">Loading...</Text>
      ) : requests.length === 0 ? (
        <Text type="secondary">No pending requests.</Text>
      ) : (
        <List
          dataSource={requests}
          renderItem={(email) => (
            <List.Item
              actions={[
                <Popconfirm
                  title={`Approve access for ${email}?`}
                  onConfirm={() => handleApprove(email)}
                  okText="Approve"
                  cancelText="Cancel"
                >
                  <AntButton
                    type="primary"
                    icon={<CheckOutlined />}
                    size="small"
                    loading={processing[email] === "approving"}
                  >
                    Approve
                  </AntButton>
                </Popconfirm>,
                <Popconfirm
                  title={`Reject request from ${email}?`}
                  onConfirm={() => handleReject(email)}
                  okText="Reject"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <AntButton
                    danger
                    icon={<CloseOutlined />}
                    size="small"
                    loading={processing[email] === "rejecting"}
                  >
                    Reject
                  </AntButton>
                </Popconfirm>,
              ]}
            >
              <Space>
                <Tag color="orange">Pending</Tag>
                <Text>{email}</Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

function Users() {
  return (
    <div>
      <Container fluid>
        <Row>
          <Col lg={12}>
            <AccessRequests />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <UsersTable />
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <ManageUsers is_admin={false} />
          </Col>
          <Col lg={6}>
            <ManageUsers is_admin={true} />
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Users
